import React, { FC, useCallback, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { ProcurementsDisplay } from 'src/models/procurements/types';
import { WorkspacePicker, RejectTendersButton } from 'src/shared';
import useOverviewMode from 'src/reactiveVars/overviewModeVar';
import { BidItemType } from '@tendium/prom-types/tender';
import { IProcToRejecting } from 'src/shared/Tender/RejectTendersButton';
import useSelectedTenders from 'src/reactiveVars/SelectedTendersVar';
import { Checkbox } from 'src/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-light-svg-icons';
import {
  useBidIdsFromSelectedTenders,
  useOriginsIdsFromSelectedTenders,
  useReadUnreadIdsFromSelectedTenders
} from 'src/models/procurements/Tenders/hooks';
import { useTranslation } from 'react-i18next';
import { useCloseSidebar } from 'src/shared/InfoSidebar/hooks';
import { useUpdateIsReadTenders } from 'src/models/procurements/hooks';
import { useTendersBoard } from 'src/models/procurements/TendersViews';

export const OverviewBoardActionsMultiple: FC = () => {
  const { data: tendersData } = useTendersBoard();
  const allTenders = useMemo(() => tendersData?.tenders ?? [], [tendersData]);
  const { t } = useTranslation();

  const procurementTabInfo = useOverviewMode();
  const [selectedTenders, setSelectedTenders] = useSelectedTenders();
  const originIds = useOriginsIdsFromSelectedTenders();
  const bidIds = useBidIdsFromSelectedTenders();
  const onCloseSidebar = useCloseSidebar();

  const clearSelectedTenders = useCallback(() => {
    onCloseSidebar();
    setSelectedTenders([]);
  }, [onCloseSidebar, setSelectedTenders]);

  const tendersToRejecting: IProcToRejecting[] = useMemo(
    () =>
      selectedTenders.map(tender => {
        const { id, isRejected } = tender;
        return { id, isRejected };
      }),
    [selectedTenders]
  );

  const isMatchedTab = useMemo(() => {
    return procurementTabInfo?.tab === ProcurementsDisplay.MATCHED;
  }, [procurementTabInfo]);

  const [definite, setDefinite] = useState(false);

  const onSelectAllTenders = useCallback(
    e => {
      const isSelected = e.target.checked;
      setDefinite(isSelected);
      setSelectedTenders(isSelected ? allTenders : []);
    },
    [allTenders, setSelectedTenders]
  );

  const [updateTendersAsRead] = useUpdateIsReadTenders();

  const { selectedIsReadIds, selectedIsUnReadIds } = useReadUnreadIdsFromSelectedTenders();
  const isSelectedSomeUnread = !!selectedIsUnReadIds.length;

  const changeReadStatusSelectedProcurements = useCallback(() => {
    const tenderIds = isSelectedSomeUnread ? selectedIsUnReadIds : selectedIsReadIds;

    updateTendersAsRead({
      tenderIds,
      isRead: isSelectedSomeUnread,
      onFinish: clearSelectedTenders
    });
  }, [clearSelectedTenders, isSelectedSomeUnread, selectedIsReadIds, selectedIsUnReadIds, updateTendersAsRead]);

  return (
    <ul className={styles.actions}>
      <li className={styles.action}>
        <Checkbox
          indeterminate={!!selectedTenders.length && !definite}
          onChange={onSelectAllTenders}
          className={styles.checkbox}
        />
      </li>
      <li className={styles.action}>
        <WorkspacePicker
          bidIds={bidIds}
          originIds={originIds}
          originType={BidItemType.Procurement}
          eventSource={'OverviewMultipleTenders'}
          onChange={clearSelectedTenders}
          isSelected={!!selectedTenders.length}
          mode={'medium'}
        />
      </li>

      {isMatchedTab && (
        <li className={styles.action}>
          <RejectTendersButton
            procurements={tendersToRejecting}
            onFinish={clearSelectedTenders}
            eventSource={'OverviewActionsMultiple'}
            mode={'medium'}
          />
        </li>
      )}
      <li className={styles.action}>
        <button onClick={changeReadStatusSelectedProcurements} className={styles.readButton}>
          <FontAwesomeIcon icon={faEye} />
          <span className={styles.readButtonTitle}>
            {t(isSelectedSomeUnread ? 'Tenders.markAsRead' : 'Tenders.markAsUnread')}
          </span>
        </button>
      </li>
    </ul>
  );
};
export default OverviewBoardActionsMultiple;
